import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  classifyFieldStatus,
  FIELD_STATUS,
  getFieldLabel,
  humanizeFieldName
} from './docIntelUtils';

/**
 * Lovable-style agreement canvas: branded cover strip + body with
 * colour-coded field chips so users see what fills from the deal.
 * Modes: Tags (chips) | Filled (merged sample/deal) | Edit.
 */
const DocIntelStudioPreview = ({
  content = '',
  filledHtml = null,
  filledPending = false,
  viewMode = 'tags',
  onViewModeChange,
  templateName = 'Agreement for Sale',
  branding = {},
  fieldMappings = [],
  sampleLeaks = [],
  templateTables = [],
  onDropField,
  editSlot = null,
  disabled = false,
  highlightValue = null,
  highlightField = null,
  previewDealLabel = null,
  onVisibleFieldChange
}) => {
  const accent = branding.accentColor || '#0A6C5A';
  const companyName = branding.companyName || 'Your company';
  const projectName = branding.projectName || 'Project';
  const logoUrl = branding.logoUrl;
  const initial = (companyName || 'V').trim().charAt(0).toUpperCase();
  const bodyRef = useRef(null);
  const scrollRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const tableTitleById = useMemo(() => {
    const map = new Map();
    for (const table of templateTables) {
      if (table?.id) map.set(table.id, table.title || humanizeFieldName(table.id));
    }
    return map;
  }, [templateTables]);

  const displayHtml = useMemo(() => {
    if (viewMode === 'filled') {
      return filledHtml || '';
    }
    return (content || '').replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_m, name) => {
      const status = classifyFieldStatus(name, fieldMappings, sampleLeaks);
      const meta = FIELD_STATUS[status] || FIELD_STATUS.from_deal;
      if (name.startsWith('table.')) {
        const id = name.slice('table.'.length);
        const title = tableTitleById.get(id) || humanizeFieldName(id);
        return (
          `<span data-field="${name}" style="display:inline-block;margin:4px 0;padding:6px 12px;` +
          `border-radius:8px;font-size:12px;font-weight:600;${meta.chipStyle}">` +
          `⊞ ${title} — filled from the deal</span>`
        );
      }
      return (
        `<span data-field="${name}" style="display:inline-block;padding:2px 8px;border-radius:6px;` +
        `font-size:0.85em;font-weight:600;white-space:nowrap;${meta.chipStyle}">` +
        `${getFieldLabel(name)}</span>`
      );
    });
  }, [content, filledHtml, viewMode, fieldMappings, sampleLeaks, tableTitleById]);

  useEffect(() => {
    if (!highlightValue || !bodyRef.current || viewMode === 'edit') return;
    const root = bodyRef.current;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    let target = null;
    while (node) {
      if (node.nodeValue && node.nodeValue.includes(highlightValue)) {
        target = node.parentElement;
        break;
      }
      node = walker.nextNode();
    }
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const prev = target.style.outline;
    const prevBg = target.style.backgroundColor;
    target.style.outline = '2px solid #d97706';
    target.style.backgroundColor = '#fef3c7';
    const t = window.setTimeout(() => {
      target.style.outline = prev;
      target.style.backgroundColor = prevBg;
    }, 2200);
    return () => window.clearTimeout(t);
  }, [highlightValue, displayHtml, viewMode]);

  useEffect(() => {
    if (!highlightField || !bodyRef.current || viewMode === 'edit') return;
    const el = bodyRef.current.querySelector(`[data-field="${highlightField}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const prev = el.style.outline;
    el.style.outline = '2px solid #0f766e';
    const t = window.setTimeout(() => {
      el.style.outline = prev;
    }, 1600);
    return () => window.clearTimeout(t);
  }, [highlightField, displayHtml, viewMode]);

  useEffect(() => {
    const scroller = scrollRef.current;
    const root = bodyRef.current;
    if (!scroller || !root || viewMode === 'edit' || !onVisibleFieldChange) return undefined;

    let raf = 0;
    const report = () => {
      const chips = root.querySelectorAll('[data-field]');
      if (!chips.length) {
        onVisibleFieldChange(null);
        return;
      }
      const mid = scroller.getBoundingClientRect().top + scroller.clientHeight * 0.35;
      let best = null;
      let bestDist = Infinity;
      chips.forEach((chip) => {
        const rect = chip.getBoundingClientRect();
        const dist = Math.abs(rect.top + rect.height / 2 - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = chip.getAttribute('data-field');
        }
      });
      onVisibleFieldChange(best);
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(report);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    report();
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [displayHtml, viewMode, onVisibleFieldChange]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const token = e.dataTransfer.getData('text/plain');
    if (!token || !token.includes('{{')) return;
    onDropField?.(token);
  };

  const modeBtn = (mode, label) => (
    <button
      key={mode}
      type="button"
      disabled={disabled && mode === 'edit'}
      onClick={() => onViewModeChange?.(mode)}
      className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
        viewMode === mode
          ? 'bg-[#2A565E] text-white'
          : 'text-slate-600 hover:bg-white'
      } disabled:opacity-40`}>
      {label}
    </button>
  );

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-2.5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800">
            {templateName || 'Agreement for Sale'} — Modern layout
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-700">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> From deal
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Needs review
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-sky-500" /> You fill in
            </span>
            <span className="font-medium text-slate-500">· drag fields from the right</span>
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100/80 p-0.5">
          {modeBtn('tags', 'Tags')}
          {modeBtn('filled', 'Filled')}
          {modeBtn('edit', 'Edit')}
        </div>
      </div>

      {viewMode === 'edit' ? (
        <div className="p-3">{editSlot}</div>
      ) : (
        <div
          ref={scrollRef}
          className={`max-h-[calc(100vh-220px)] overflow-y-auto bg-[#e8ebe6] p-4 sm:p-6 transition ${
            dragOver ? 'ring-2 ring-inset ring-teal-500 ring-offset-2' : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}>
          <div
            className="mx-auto mb-6 max-w-3xl overflow-hidden rounded-sm bg-[#f7f4ef] shadow-lg ring-1 ring-black/5"
            style={{ borderTop: `6px solid ${accent}` }}>
            <div className="px-8 pb-10 pt-8 sm:px-12 sm:pt-10">
              <div className="flex items-start gap-4">
                {logoUrl && branding.includeLogo !== false ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="h-14 w-14 rounded-md object-contain"
                  />
                ) : (
                  <div
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md text-2xl font-bold text-white"
                    style={{ backgroundColor: accent }}>
                    {initial}
                  </div>
                )}
                <div className="min-w-0">
                  <h2
                    className="font-serif text-2xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-3xl"
                    style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    {companyName}
                  </h2>
                  <p
                    className="mt-1 text-[11px] font-semibold uppercase tracking-[0.28em]"
                    style={{ color: accent }}>
                    {projectName}
                  </p>
                </div>
              </div>

              <div className="my-10 flex items-center gap-4">
                <div className="h-px flex-1" style={{ backgroundColor: `${accent}55` }} />
                <p
                  className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.22em]"
                  style={{ color: accent }}>
                  Agreement for Sale
                </p>
                <div className="h-px flex-1" style={{ backgroundColor: `${accent}55` }} />
              </div>

              <h3
                className="font-serif text-3xl font-semibold text-slate-900 sm:text-4xl"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                {templateName || 'Agreement'}
              </h3>
              <p className="mt-2 text-sm text-slate-500">{projectName}</p>

              <div className="mt-10 grid gap-6 sm:grid-cols-2">
                <div>
                  <p
                    className="text-xs font-bold uppercase tracking-[0.18em]"
                    style={{ color: accent }}>
                    Promoter
                  </p>
                  <p className="mt-1 font-serif text-lg text-slate-900">{companyName}</p>
                  {branding.address ? (
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{branding.address}</p>
                  ) : null}
                </div>
                <div>
                  <p
                    className="text-xs font-bold uppercase tracking-[0.18em]"
                    style={{ color: accent }}>
                    Allottee
                  </p>
                  <p className="mt-1 font-serif text-lg font-semibold text-slate-800">
                    {viewMode === 'filled' ? 'As filled below' : 'Filled from each deal'}
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-700">
                    Name, address, KYC → auto
                  </p>
                </div>
              </div>

              {(branding.email || branding.phone || branding.gstin) && (
                <p className="mt-10 border-t border-slate-200/80 pt-4 text-[11px] text-slate-500">
                  {[branding.email, branding.phone, branding.gstin ? `GSTIN ${branding.gstin}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
            </div>
          </div>

          <div ref={bodyRef} className="relative mx-auto max-w-3xl">
            {viewMode === 'filled' && filledPending ? (
              <div className="flex items-center justify-center gap-2 rounded-sm bg-white px-8 py-16 text-sm text-slate-500 shadow-lg ring-1 ring-black/5">
                <Loader2 className="h-4 w-4 animate-spin" />
                Filling sample values…
              </div>
            ) : (
              <div
                className="prose prose-sm rounded-sm bg-white px-8 py-10 shadow-lg ring-1 ring-black/5 prose-headings:font-serif prose-p:leading-relaxed sm:px-12"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
                dangerouslySetInnerHTML={{ __html: displayHtml }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocIntelStudioPreview;
